import { usePathname } from "next/navigation";
import { useState, useEffect, useCallback } from "react";

type ImmersiveNavState = {
  isVisible: boolean;
  isImmersive: boolean;
};

type UseImmersiveNavProps = {
  isDrawerOpen?: boolean;
  isSearchDropdownOpen?: boolean;
  isContentNavTransparent?: boolean;
};

export const useImmersiveNav = ({
  isDrawerOpen = false,
  isSearchDropdownOpen = false,
  isContentNavTransparent = false,
}: UseImmersiveNavProps = {}) => {
  const pathname = usePathname();
  const path = pathname?.split("?")[0];

  const isHomepage = (() => {
    if (!path) return false;

    if (path === "/" || path === "/gb" || path === "/gb/") return true;

    if (path === "/topshop" || path === "/topman") return true;

    const pathSegments = path.split("/").filter(Boolean);
    if (
      pathSegments.length === 2 &&
      ["topshop", "topman"].includes(pathSegments[1] as string)
    )
      return true;

    return false;
  })();

  const [state, setState] = useState<ImmersiveNavState>({
    isVisible: true,
    isImmersive: isHomepage || isContentNavTransparent,
  });

  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMouseEnter = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isImmersive: false,
    }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (
      isDrawerOpen ||
      isSearchDropdownOpen ||
      (currentScrollY <= 0 && !isHomepage && !isContentNavTransparent)
    )
      return;

    setState((prevState) => ({
      ...prevState,
      isImmersive: true,
    }));
  }, [isDrawerOpen, isSearchDropdownOpen, isHomepage, isContentNavTransparent]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY;
      const isScrollingUp = currentScrollY < lastScrollY;
      const isAtTop = currentScrollY <= 0;

      setState((prevState) => {
        if (isDrawerOpen || isSearchDropdownOpen) {
          return { isVisible: true, isImmersive: false };
        }

        if (isAtTop) {
          return {
            isVisible: true,
            isImmersive: isHomepage || isContentNavTransparent,
          };
        }

        if (isScrollingDown) {
          return { ...prevState, isVisible: false };
        }

        if (isScrollingUp) {
          return { isVisible: true, isImmersive: true };
        }

        return prevState;
      });

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [
    lastScrollY,
    isDrawerOpen,
    isSearchDropdownOpen,
    isHomepage,
    isContentNavTransparent,
  ]);

  useEffect(() => {
    if (isDrawerOpen || isSearchDropdownOpen) {
      setState((prevState) => ({
        ...prevState,
        isImmersive: false,
      }));
    }
  }, [isDrawerOpen, isSearchDropdownOpen]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isImmersive:
        (isHomepage || isContentNavTransparent) && window.scrollY <= 0,
    }));
  }, [isHomepage, isContentNavTransparent]);

  return {
    isVisible: state.isVisible,
    isImmersive: state.isImmersive,
    isHomepage,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
  };
};
